require('../scss/app.scss');

// Bootstrap components needed on (almost) every page are imported statically for
// their data-attribute auto-init: navbar collapse, dropdowns, search modal.
// Import the individual dist files, not the 'bootstrap' barrel — the barrel is a
// single pre-bundled ESM file that cannot be tree-shaken and drags in every
// component (see Lighthouse "unused JavaScript").
// Carousel and Swiper are loaded on demand below, only on pages that use them.
import 'bootstrap/js/dist/modal';
import 'bootstrap/js/dist/collapse';
import 'bootstrap/js/dist/dropdown';

// ── Idle scheduler ─────────────────────────────────────────────────────────────
// Runs non-critical work (analytics, cookie banner, slider wiring) only after
// the page has fully loaded AND the main thread is idle, keeping it out of the
// LCP/TBT window that PageSpeed measures.
function runWhenIdle(fn, timeout = 2000) {
    const schedule = function () {
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(fn, { timeout });
        } else {
            window.setTimeout(fn, 200);
        }
    };
    if (document.readyState === 'complete') {
        schedule();
    } else {
        window.addEventListener('load', schedule, { once: true });
    }
}

// ── Google Analytics (always loaded, independent of the cookie banner) ─────────
// Deferred to post-load idle: gtag.js is ~147 KiB of third-party JS that would
// otherwise compete with the LCP image and fonts during the critical window.
// The inline dataLayer stub in base.html.twig queues events until it arrives.
let gaLoaded = false;
function loadGoogleAnalytics() {
    if (gaLoaded) return;
    const id = window.gaMeasurementId;
    if (!id || typeof window.gtag !== 'function') return;
    gaLoaded = true;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + id;
    document.head.appendChild(script);

    window.gtag('config', id);
}

runWhenIdle(loadGoogleAnalytics);

// ── Cookie Consent (informational only — no analytics opt-in/opt-out) ──────────
// Dynamic import (lazy chunk for JS *and* CSS), shown on the FIRST user
// interaction, or 15 s after load at the latest. LCP keeps updating until the
// first interaction, so a late large paint of the banner can itself become the
// LCP element and tank the score; first interaction finalizes LCP, which makes
// showing the banner at that moment always safe. The banner gates nothing.
let cookieConsentStarted = false;
function startCookieConsent() {
    if (cookieConsentStarted) return;
    cookieConsentStarted = true;
    Promise.all([
        import('vanilla-cookieconsent'),
        import('vanilla-cookieconsent/dist/cookieconsent.css'),
    ]).then(([CookieConsent]) => {
        initCookieConsent(CookieConsent);
    });
}
['pointerdown', 'keydown', 'touchstart', 'wheel', 'scroll'].forEach(function (evt) {
    window.addEventListener(evt, startCookieConsent, { once: true, passive: true });
});
if (document.readyState === 'complete') {
    window.setTimeout(startCookieConsent, 15000);
} else {
    window.addEventListener('load', function () {
        window.setTimeout(startCookieConsent, 15000);
    }, { once: true });
}

function initCookieConsent(CookieConsent) {
    CookieConsent.run({
    guiOptions: {
        consentModal: {
            layout: 'box',
            position: 'bottom left',
        },
        preferencesModal: {
            layout: 'box',
        },
    },
    categories: {
        necessary: {
            enabled: true,
            readOnly: true,
        },
    },
    language: {
        default: 'bg',
        autoDetect: 'document',
        translations: {
            bg: {
                consentModal: {
                    title: 'Използваме бисквитки',
                    description: 'Сайтът използва бисквитки, включително Google Analytics за анонимна статистика на посещенията, за да функционира правилно и да подобряваме съдържанието.',
                    acceptAllBtn: 'Разбрах',
                    showPreferencesBtn: 'Настройки',
                },
                preferencesModal: {
                    title: 'Информация за бисквитките',
                    acceptAllBtn: 'Разбрах',
                    savePreferencesBtn: 'Запазване',
                    closeIconLabel: 'Затвори',
                    sections: [
                        {
                            title: 'Необходими бисквитки',
                            description: 'Необходими за правилното функциониране на сайта. Не могат да бъдат изключени.',
                            linkedCategory: 'necessary',
                        },
                        {
                            title: 'Аналитични бисквитки',
                            description: 'Използваме Google Analytics за анонимна статистика на посещенията. Помага ни да подобряваме съдържанието.',
                        },
                    ],
                },
            },
            en: {
                consentModal: {
                    title: 'We use cookies',
                    description: 'This site uses cookies, including Google Analytics for anonymous traffic statistics, to function correctly and to help us improve our content.',
                    acceptAllBtn: 'Got it',
                    showPreferencesBtn: 'Preferences',
                },
                preferencesModal: {
                    title: 'Cookie information',
                    acceptAllBtn: 'Got it',
                    savePreferencesBtn: 'Save preferences',
                    closeIconLabel: 'Close',
                    sections: [
                        {
                            title: 'Necessary cookies',
                            description: 'Required for the site to function correctly. Cannot be disabled.',
                            linkedCategory: 'necessary',
                        },
                        {
                            title: 'Analytics cookies',
                            description: 'We use Google Analytics for anonymous traffic statistics to help improve our content.',
                        },
                    ],
                },
            },
            de: {
                consentModal: {
                    title: 'Wir verwenden Cookies',
                    description: 'Diese Website verwendet Cookies, einschließlich Google Analytics für anonyme Besucherstatistiken, um ordnungsgemäß zu funktionieren und unsere Inhalte zu verbessern.',
                    acceptAllBtn: 'Verstanden',
                    showPreferencesBtn: 'Einstellungen',
                },
                preferencesModal: {
                    title: 'Cookie-Informationen',
                    acceptAllBtn: 'Verstanden',
                    savePreferencesBtn: 'Einstellungen speichern',
                    closeIconLabel: 'Schließen',
                    sections: [
                        {
                            title: 'Notwendige Cookies',
                            description: 'Erforderlich für die ordnungsgemäße Funktion der Website. Können nicht deaktiviert werden.',
                            linkedCategory: 'necessary',
                        },
                        {
                            title: 'Analyse-Cookies',
                            description: 'Wir verwenden Google Analytics für anonyme Besucherstatistiken zur Verbesserung unserer Inhalte.',
                        },
                    ],
                },
            },
        },
    },
    });
}

// ── Bootstrap Carousel (loaded on demand, only on pages that have one) ─────────
// Importing the module after DOMContentLoaded skips Bootstrap's `data-bs-ride`
// auto-init, so instantiate each carousel manually once the module is ready.
if (document.querySelector('.carousel')) {
    import('bootstrap/js/dist/carousel').then(({ default: Carousel }) => {
        document.querySelectorAll('.carousel').forEach((el) => {
            Carousel.getOrCreateInstance(el);
        });
    });
}

// ── Fancybox (loaded on demand, only on pages that have a gallery) ─────────────
if (document.querySelector('[data-fancybox]')) {
    Promise.all([
        import('@fancyapps/ui'),
        import('@fancyapps/ui/dist/fancybox.css'),
    ]).then(([{ Fancybox }]) => {
        Fancybox.bind('[data-fancybox]');
    });
}

// ── DOM ready ─────────────────────────────────────────────────────────────────
function onReady(fn) {
    if (document.readyState !== 'loading') {
        fn();
    } else {
        document.addEventListener('DOMContentLoaded', fn);
    }
}

onReady(function () {
    // Back to top
    const backToTop = document.getElementById('back-to-top');
    if (backToTop) {
        backToTop.style.display = 'none';
        backToTop.addEventListener('click', function (event) {
            event.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }

    // Hero Swiper (homepage only). The first slide is server-rendered static HTML
    // with an eager LCP image, so deferring the Swiper JS doesn't delay the LCP —
    // it only wires up sliding/autoplay a moment later. Keeps ~22 KiB of Swiper
    // out of the main bundle on every non-homepage request, and loading it at
    // post-load idle keeps Swiper's own init-time layout reads (its measured
    // forced reflows) out of the TBT window too.
    const heroSwiperEl = document.getElementById('hero-swiper');
    if (heroSwiperEl) {
        runWhenIdle(function () {
            import('./hero-swiper').then(({ default: initHeroSwiper }) => {
                initHeroSwiper(heroSwiperEl);
            });
        });
    }

    // Scroll-driven UI (back-to-top visibility + sticky-nav shadow) via an
    // IntersectionObserver on a 1px sentinel at the top of the document instead
    // of scroll listeners: no window.scrollY reads at all, so no forced reflows
    // (Lighthouse attributed the app.js reflow time to the scrollY read even
    // inside a rAF callback — it flushes layout invalidated earlier in the frame).
    const stickyNav = document.querySelector('.navbar-main');
    if ((backToTop || stickyNav) && 'IntersectionObserver' in window) {
        const sentinel = document.createElement('div');
        sentinel.setAttribute('aria-hidden', 'true');
        sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;visibility:hidden;';
        document.body.prepend(sentinel);

        if (backToTop) {
            // Sentinel counts as "visible" until scrolled 200px past it.
            new IntersectionObserver(function (entries) {
                backToTop.style.display = entries[0].isIntersecting ? 'none' : 'flex';
            }, { rootMargin: '200px 0px 0px 0px' }).observe(sentinel);
        }
        if (stickyNav) {
            new IntersectionObserver(function (entries) {
                stickyNav.classList.toggle('is-scrolled', !entries[0].isIntersecting);
            }, { rootMargin: '4px 0px 0px 0px' }).observe(sentinel);
        }
    }

    // Scroll-reveal for cards (replaces the old load-time bounce-in-fwd animation)
    const revealTargets = document.querySelectorAll('.bounce-in-fwd');
    if (revealTargets.length && 'IntersectionObserver' in window) {
        const revealObserver = new IntersectionObserver(function (entries, observer) {
            entries.forEach(function (entry) {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                    observer.unobserve(entry.target);
                }
            });
        }, { threshold: 0.15 });
        revealTargets.forEach(function (el) { revealObserver.observe(el); });
    } else {
        revealTargets.forEach(function (el) { el.classList.add('is-visible'); });
    }

    // Quote form validation
    const quoteForm = document.getElementById('quote-form');
    if (quoteForm) {
        quoteForm.addEventListener('submit', function (event) {
            if (quoteForm.querySelectorAll("[name='quote[services][]']:checked").length < 1) {
                alert(window.quote_message_alert);
                event.preventDefault();
            }
        });
    }
});
